/**
 * roofSubtype.js — shared roof-subtype resolver.
 *
 * v10.21 (Feb 2026 · UAT A999614/A999615/A999616 · Iron misroute)
 * ─────────────────────────────────────────────────────────────
 * The Repairs / Restoration builder tabs branch on
 *   isIronSubtype(appraisal.roof_subtype)
 *   isTinTileSubtype(appraisal.roof_subtype)
 * when deciding which sub-builder to render. Historically the
 * `roof_subtype` field on the appraisal was expected to be one of:
 *   'Iron' · 'Modern Concrete' · 'Old Concrete' · 'Clay' ·
 *   'Decramastic' · 'Colortile'
 *
 * UAT surfaced that on Iron-roof intakes the appraisal shipped WITHOUT
 * a `roof_subtype` value — the property's `roof_details` only had
 * `roof_type='Iron'` + `material_type='Galv'`, and neither the intake
 * form nor the spawn-child inheritance filled it in. Result: both the
 * parent and its children fell through to the Concrete "not applicable"
 * fallback card.
 *
 * This helper resolves a canonical `roof_subtype` value from every
 * signal we already have on the appraisal DTO, so the branching works
 * even when the canonical field is blank. Priority (highest → lowest):
 *   1. appraisal.roof_subtype             (authoritative if set)
 *   2. bundle.meta.roof_subtype           (BE-derived, sometimes richer)
 *   3. appraisal.primary_roof_type_hint   ("Iron" / "Concrete" / …)
 *   4. appraisal.primary_material         ("Long Run", "Concrete Tile" …)
 *   5. appraisal.material_type            ("Galv" / "Colorsteel" → Iron)
 *
 * The mapper returns a value guaranteed to be recognised by the three
 * `isIronSubtype` / `isTinTileSubtype` / `is_concrete` gates in the
 * builders. Unknown/blank inputs return `''` (existing default), so
 * this helper only ever WIDENS the recognition — it never reclassifies
 * a stored subtype.
 */

const IRON_TOKENS = ['iron', 'long run', 'long-run', 'longrun', 'colorsteel', 'color steel', 'steel'];
const TIN_TILE_TOKENS = ['decramastic', 'decra', 'colortile', 'color tile', 'colourtile', 'colour tile', 'tin tile', 'chipped tin'];
const CLAY_TOKENS = ['clay'];
const CONCRETE_TOKENS = ['concrete tile', 'modern concrete', 'old concrete', 'concrete'];
const GALV_TOKENS = ['galv', 'zincalume', 'zincalum', 'colorbond'];

const _has = (haystack, tokens) => {
  const s = String(haystack || '').toLowerCase().trim();
  if (!s) return false;
  return tokens.some(t => s.includes(t));
};

/** Map any free-form roof hint to a canonical subtype key the builders
 *  recognise. Returns '' when we can't confidently classify. */
export function mapHintToSubtype(hint, materialHint = '') {
  if (_has(hint, TIN_TILE_TOKENS)) {
    // Prefer the more specific label when it's already spelled out.
    if (_has(hint, ['colortile', 'color tile', 'colourtile', 'colour tile'])) return 'Colortile';
    return 'Decramastic';
  }
  if (_has(hint, CLAY_TOKENS)) return 'Clay';
  if (_has(hint, CONCRETE_TOKENS)) {
    if (_has(hint, ['old'])) return 'Old Concrete';
    return 'Modern Concrete';
  }
  if (_has(hint, IRON_TOKENS) || _has(materialHint, IRON_TOKENS) || _has(materialHint, GALV_TOKENS)) return 'Iron';
  return '';
}

/** Given an appraisal DTO (and optional bundle.meta), return the
 *  effective roof_subtype that should drive builder branching. */
export function resolveEffectiveRoofSubtype(appraisal, bundleMeta = null) {
  const canonical = (appraisal?.roof_subtype || '').toString().trim();
  if (canonical) return canonical;
  const metaSubtype = (bundleMeta?.roof_subtype || '').toString().trim();
  if (metaSubtype) return metaSubtype;
  const derived = mapHintToSubtype(
    appraisal?.primary_roof_type_hint,
    appraisal?.primary_material,
  );
  if (derived) return derived;
  const fromMaterialType = mapHintToSubtype('', appraisal?.material_type);
  return fromMaterialType || '';
}
